import { ArrowLeft, ArrowRight, Check, ChevronRight, Lightbulb } from 'lucide-react';
import { useId, useState } from 'react';

import { Button } from '../../button';

import { Lab } from './lab-parts';

import type { StudyAid } from '@mio/shared';

import './reading-aids.css';

export type MapDesign = 'path' | 'outline' | 'hub' | 'matrix';
export type CardDesign = 'reveal' | 'compare' | 'notebook' | 'steps';

export function ConceptMap({
  aid,
  design = 'path',
}: {
  aid: Pick<StudyAid, 'question' | 'branches'>;
  design?: MapDesign;
}) {
  const [selected, setSelected] = useState(0);
  const detailId = useId();
  const branch = aid.branches[selected] ?? aid.branches[0];
  if (!branch) return null;
  const choices = aid.branches.map((entry, index) => (
    <Button
      key={entry.label}
      variant="ghost"
      className="study-map-node"
      aria-pressed={selected === index}
      aria-controls={detailId}
      onClick={() => setSelected(index)}
    >
      <span className="study-node-number">{String(index + 1).padStart(2, '0')}</span>
      <span>
        <small>{entry.relation}</small>
        <strong>{entry.label}</strong>
      </span>
      <ChevronRight size={16} aria-hidden="true" />
    </Button>
  ));
  return (
    <Lab
      title="Connect the ideas"
      eyebrow="Explore the connections"
      className="cb-study"
      note="Select an idea. Explain the problem it solves and what would happen without it."
    >
      <div className={`study-map study-map-${design}`}>
        <p className="study-map-question">{aid.question}</p>
        <div className="study-map-layout">
          <div className="study-map-choices" role="group" aria-label="Concept connections">
            {choices}
          </div>
          <div className="study-map-detail" id={detailId} aria-live="polite" aria-atomic="true">
            <span className="study-kicker">
              Connection {selected + 1} of {aid.branches.length}
            </span>
            <h4>{branch.label}</h4>
            <p>{branch.why}</p>
            <div className="study-apply">
              <Lightbulb size={16} aria-hidden="true" />
              <div>
                <strong>Make it concrete</strong>
                <p>{branch.apply}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Lab>
  );
}

export function RecallCards({
  aid,
  design = 'reveal',
}: {
  aid: Pick<StudyAid, 'cards'>;
  design?: CardDesign;
}) {
  const { cards } = aid;
  const [index, setIndex] = useState(0);
  const [revealed, setRevealed] = useState(false);
  const [stage, setStage] = useState(0);
  const [drafts, setDrafts] = useState<Record<number, string>>({});
  const answerId = useId();
  const draftId = useId();
  const card = cards[index] ?? cards[0];
  if (!card) return null;
  function select(next: number) {
    setIndex(next);
    setRevealed(false);
    setStage(0);
  }
  const showAnswer = design === 'steps' ? stage > 0 : revealed;
  return (
    <Lab title="Explain it before you build it" eyebrow="Pause and predict" className="cb-study">
      <div className={`study-cards study-cards-${design}`}>
        <div className="study-card-top">
          <span className="study-kicker" aria-live="polite">
            Card {index + 1} of {cards.length}
          </span>
        </div>
        {design === 'notebook' && (
          <div className="study-card-tabs" role="group" aria-label="Choose a scenario">
            {cards.map((_, number) => (
              <Button
                key={number}
                variant={index === number ? 'secondary' : 'ghost'}
                size="sm"
                aria-pressed={index === number}
                onClick={() => select(number)}
              >
                Scenario {number + 1}
              </Button>
            ))}
          </div>
        )}
        {design === 'steps' && (
          <ol className="study-card-steps" aria-label="Reasoning stages">
            {['Predict', 'Explain', 'Apply'].map((label, number) => (
              <li key={label} aria-current={stage === number ? 'step' : undefined}>
                <span>{stage > number ? <Check size={12} aria-hidden="true" /> : number + 1}</span>
                {label}
              </li>
            ))}
          </ol>
        )}
        <p className="study-card-question">{card.question}</p>
        <div className="study-card-body">
          {design !== 'reveal' && (
            <div className="study-prediction">
              <label htmlFor={draftId}>
                Your explanation <span>(optional)</span>
              </label>
              <textarea
                id={draftId}
                rows={3}
                value={drafts[index] ?? ''}
                onChange={(event) => setDrafts({ ...drafts, [index]: event.target.value })}
                placeholder="What happens, and why?"
                className="border-line-strong bg-surface-raised text-fg placeholder:text-fg-subtle focus-visible:border-ring focus-visible:ring-ring/15 w-full resize-y rounded-md border px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2"
              />
            </div>
          )}
          <div className="study-answer-space">
            {!showAnswer && design === 'compare' && (
              <p className="study-answer-placeholder">
                Make a prediction first.
                <br />
                Then compare the reasoning.
              </p>
            )}
            <div
              id={answerId}
              hidden={!showAnswer}
              className="study-card-answer"
              aria-live="polite"
            >
              <span className="study-kicker">Reason it through</span>
              <p>{card.answer}</p>
              <div hidden={design === 'steps' && stage < 2} className="study-apply">
                <Lightbulb size={16} aria-hidden="true" />
                <div>
                  <strong>Try another case</strong>
                  <p>{card.transfer}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="study-card-actions">
          {design === 'steps' ? (
            <Button
              size="sm"
              variant="secondary"
              aria-controls={answerId}
              onClick={() => setStage(stage === 2 ? 0 : stage + 1)}
            >
              {stage === 0
                ? 'Reveal explanation'
                : stage === 1
                  ? 'Try another case'
                  : 'Predict again'}
              <ArrowRight size={14} aria-hidden="true" />
            </Button>
          ) : (
            <Button
              size="sm"
              variant="secondary"
              aria-expanded={revealed}
              aria-controls={answerId}
              onClick={() => setRevealed(!revealed)}
            >
              <Lightbulb size={14} aria-hidden="true" />
              {revealed ? 'Hide explanation' : 'Reveal explanation'}
            </Button>
          )}
        </div>
        <div className="study-card-navigation">
          <Button
            variant="ghost"
            size="sm"
            disabled={index === 0}
            onClick={() => select(index - 1)}
          >
            <ArrowLeft size={14} aria-hidden="true" />
            Previous card
          </Button>
          <span aria-hidden="true" className="study-dots">
            {cards.map((_, number) => (
              <i key={number} data-current={number === index} />
            ))}
          </span>
          <Button variant="ghost" size="sm" onClick={() => select((index + 1) % cards.length)}>
            {index === cards.length - 1 ? 'Back to first card' : 'Next card'}
            <ArrowRight size={14} aria-hidden="true" />
          </Button>
        </div>
      </div>
    </Lab>
  );
}
